import type { FormEvent, JSX } from 'react'

import { AuthFormBoxPaddings, AuthFormBoxRowGaps } from "/app/libraries/arcana/src/blocks/AuthFormBox/index.ts"
import { AuthFormBoxTemplate } from "/app/libraries/arcana/src/blocks/AuthFormBox/Templates/index.ts"
import { PrimarySubmitButton } from "/app/libraries/arcana/src/atoms/Button/Templates/index.ts"
import { TextBoxCaption3, TextBoxCaption4Secondary, TextBoxHeadline2 } from "/app/libraries/arcana/src/atoms/TextBox/Templates/index.ts"

import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { model as router } from '~/core/router'
import { AuthTab } from '~/features/change-auth-type'
import { AppTitle } from '~/shared/ui/components/AppTitle'
import { AuthLayout } from '~/widgets/auth'
import { useErrorMessage } from '~/widgets/Popups/MessagePortal'
import { translations, model as verifyModel } from '../model'
import { BackButton } from './BackButton'
import { LockedBlock } from './LockedBlock'
import { useCountdown } from './useCountdown'
import { VerifyCodeInput } from './VerifyCodeInput'
import { VerifyTextBlock } from './VerifyTextBlock'

export function Title(): JSX.Element {
  return <AppTitle t='account_verify' />
}

VerifyPage.displayName = 'VerifyPage'
export function VerifyPage(): JSX.Element {
  const message = useUnit(verifyModel.$message)
  const pending = useUnit(verifyModel.$pendingRegistration)
  const permanentError = useUnit(verifyModel.$permError)
  const locked = useUnit(verifyModel.$locked)
  const formTranslations = useUnit(translations.$verifyFormTranslation)
  const verifyType = useUnit(router.$search)

  const { submit, eachValid } = useForm(verifyModel.confirmForm)

  const { liveTo, liveResendIn } = useCountdown()

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const isSubmitButtonDisabled =
    pending ||
    !eachValid ||
    liveTo === null ||
    locked ||
    Boolean(permanentError)

  const titleButton = locked
    ? formTranslations.lockedButton
    : formTranslations.verifyButton

  const footerText =
    verifyType?.replace('?=', '') === AuthTab.Phone
      ? formTranslations.verifyPhoneFooter
      : formTranslations.verifyFooter

  useErrorMessage(message, 'VerifyPage')

  return (
    <AuthLayout>
      <AuthFormBoxTemplate
        rowGap={AuthFormBoxRowGaps.SIZE_20}
        padding={AuthFormBoxPaddings.SIZE_140_100}
      >
        {!locked && (
          <form
            onSubmit={onSubmit}
            autoComplete='off'
            noValidate
            style={{ marginBottom: '12px' }}
          >
            <BackButton />
            <TextBoxHeadline2 title={formTranslations.title} />
            <TextBoxCaption3 title={formTranslations.description} />
            <VerifyCodeInput liveTo={liveTo} />
            <PrimarySubmitButton
              title={titleButton}
              disabled={isSubmitButtonDisabled}
              withLoader
              isLoading={pending || locked}
            />
          </form>
        )}
        {locked && <LockedBlock />}
        {!locked && (
          <VerifyTextBlock liveTo={liveTo} liveResendIn={liveResendIn} />
        )}
        {!locked && <TextBoxCaption4Secondary title={footerText} />}
      </AuthFormBoxTemplate>
    </AuthLayout>
  )
}
